/**
 * Infer cellular carrier from ICCID using ITU-T E.118 IIN lookup (vendored list).
 * Used for operator hints and configure gates before inventory exists; CSV carrier
 * values in public.sim remain authoritative.
 */

import { identifyIssuerFromIccid, type E118IssuerMatch } from "./e118-iin-lookup.js";

/** Map ITU issuer company names to hub cell_carrier_type labels. */
const ISSUER_NAME_TO_CARRIER: Array<{ pattern: RegExp; carrier: string }> = [
  { pattern: /verizon/i, carrier: "Verizon" },
  { pattern: /vodafone/i, carrier: "Vodafone" },
  { pattern: /truphone/i, carrier: "Truphone" },
  { pattern: /t-mobile|t mobile/i, carrier: "T-Mobile" },
  { pattern: /sprint/i, carrier: "Sprint" },
  {
    pattern: /\bat\s*&?\s*t\b|at\s*&\s*t|att\b|bellsouth|cingular|tampnet/i,
    carrier: "AT&T",
  },
];

/** Legacy six-digit prefix hints when ITU lookup misses (non-standard ICCID lengths). */
const LEGACY_SIX_DIGIT_PREFIX_TO_CARRIER: Record<string, string> = {
  "891480": "Verizon",
  "898823": "Vodafone",
  "891004": "AT&T",
  "891005": "AT&T",
  "891006": "AT&T",
  "891007": "AT&T",
  "891008": "AT&T",
  "891009": "AT&T",
  "891010": "AT&T",
  "891011": "Sprint",
  "891012": "T-Mobile",
  "891013": "T-Mobile",
  "891014": "T-Mobile",
  "894447": "Truphone",
  "893027": "SaskTel",
};

export function normalizeIccidForCarrierLookup(raw: string): string {
  return String(raw ?? "").trim().toLowerCase();
}

export function iccidIssuerPrefix(iccid: string): string | null {
  const normalized = normalizeIccidForCarrierLookup(iccid);
  if (normalized.length < 6) {
    return null;
  }
  return normalized.slice(0, 6);
}

export function inferCarrierFromIssuer(issuer: E118IssuerMatch | null): string | null {
  if (!issuer) {
    return null;
  }
  const haystack = `${issuer.companyName} ${(issuer.companyURLs ?? []).join(" ")}`;
  for (const row of ISSUER_NAME_TO_CARRIER) {
    if (row.pattern.test(haystack)) {
      return row.carrier;
    }
  }
  return null;
}

export function inferCarrierFromIccid(iccid: string): string | null {
  const normalized = normalizeIccidForCarrierLookup(iccid);
  if (normalized === "") {
    return null;
  }
  const fromItu = inferCarrierFromIssuer(identifyIssuerFromIccid(normalized));
  if (fromItu) {
    return fromItu;
  }
  const legacyPrefix = iccidIssuerPrefix(normalized);
  if (legacyPrefix) {
    return LEGACY_SIX_DIGIT_PREFIX_TO_CARRIER[legacyPrefix] ?? null;
  }
  return null;
}

export function isLikelyVerizonIccid(iccid: string): boolean {
  return inferCarrierFromIccid(iccid) === "Verizon";
}

export function isLikelyVodafoneIccid(iccid: string): boolean {
  return inferCarrierFromIccid(iccid) === "Vodafone";
}

const WIRELESS_CARRIER_BY_SKU_CODE: Record<string, string> = {
  "1": "Vodafone",
  "2": "Verizon",
  "3": "AT&T",
  "4": "T-Mobile",
  "10": "SaskTel",
};

function carrierLabelFromWirelessSkuCode(code: string): string | null {
  return WIRELESS_CARRIER_BY_SKU_CODE[code] ?? null;
}

/**
 * FP-OC is the customer catalog name for the SaskTel Patrolman Plus.
 * Firmware reports that same product as FI-5A-W-10.
 */
function isSaskTelFpOcSku(sku: string): boolean {
  const upper = sku.trim().toUpperCase();
  if (upper.startsWith("ABB-")) {
    return upper.slice("ABB-".length).startsWith("FP-OC");
  }
  if (upper.startsWith("ABB ")) {
    return upper.slice("ABB ".length).startsWith("FP-OC");
  }
  return upper.startsWith("FP-OC");
}

/** Wireless SKU carrier code (`FI-5A-W-N` / `DM-1-N`: `1` Vodafone, `2` Verizon, `3` AT&T, `4` T-Mobile, `10` SaskTel). */
export function inferCarrierFromWirelessSku(sku: string | null | undefined): string | null {
  const trimmed = String(sku ?? "").trim();
  if (isSaskTelFpOcSku(trimmed)) {
    return "SaskTel";
  }
  const fiMatch = /\bFI-5A-W-(\d{1,2})\b/i.exec(trimmed);
  if (fiMatch) {
    return carrierLabelFromWirelessSkuCode(fiMatch[1]!);
  }
  const dmMatch = /\bDM-1-(\d{1,2})\b/i.exec(trimmed);
  if (dmMatch) {
    return carrierLabelFromWirelessSkuCode(dmMatch[1]!);
  }
  return null;
}

/**
 * Resolve carrier for wireless configure / SIM auto-activation.
 * Returns null when ICCID and SKU do not imply a carrier (fail closed).
 */
export function resolveWirelessSimCarrier(
  iccid: string,
  finishedSku?: string | null,
): string | null {
  return inferCarrierFromIccid(iccid) ?? inferCarrierFromWirelessSku(finishedSku) ?? null;
}

export interface WirelessIccidSkuCarrierValidation {
  ok: boolean;
  iccidCarrier: string | null;
  skuCarrier: string | null;
  message: string;
}

const WIRELESS_ICCID_FORMAT = /^[0-9]{19,20}$/;

/**
 * Ensures a scanned ICCID matches the carrier programmed by the finished SKU
 * (FI-5A-W-N / DM-1-N code, or FP-OC as firmware FI-5A-W-10 / SaskTel).
 * Validates only when both carriers are known.
 */
export function validateWirelessIccidMatchesSkuCarrier(
  iccid: string,
  finishedSku?: string | null,
): WirelessIccidSkuCarrierValidation {
  const trimmedIccid = normalizeIccidForCarrierLookup(iccid);
  if (!WIRELESS_ICCID_FORMAT.test(trimmedIccid)) {
    return { ok: true, iccidCarrier: null, skuCarrier: null, message: "" };
  }

  const iccidCarrier = inferCarrierFromIccid(trimmedIccid);
  const skuCarrier = inferCarrierFromWirelessSku(finishedSku);
  if (!iccidCarrier || !skuCarrier) {
    return { ok: true, iccidCarrier, skuCarrier, message: "" };
  }
  if (iccidCarrier === skuCarrier) {
    return { ok: true, iccidCarrier, skuCarrier, message: "" };
  }

  const skuLabel = String(finishedSku ?? "").trim();
  return {
    ok: false,
    iccidCarrier,
    skuCarrier,
    message:
      `ICCID prefix indicates ${iccidCarrier}, but this job programs ${skuCarrier}` +
      (skuLabel !== "" ? ` (${skuLabel})` : "") +
      `. Use a ${skuCarrier} SIM card.`,
  };
}

/** Throws when a carrier API activation target does not match the ICCID issuer. */
export function assertCarrierMatchesIccidForApiActivation(iccid: string, carrier: string): void {
  const normalizedCarrier = String(carrier ?? "").trim().toLowerCase();
  const inferred = inferCarrierFromIccid(iccid);
  if (normalizedCarrier === "verizon") {
    if (!isLikelyVerizonIccid(iccid)) {
      throw new Error(
        inferred
          ? `ICCID prefix indicates ${inferred}, not Verizon.`
          : "ICCID prefix does not match Verizon (expected Verizon issuer IIN).",
      );
    }
    return;
  }
  if (normalizedCarrier === "vodafone") {
    if (!isLikelyVodafoneIccid(iccid)) {
      throw new Error(
        inferred
          ? `ICCID prefix indicates ${inferred}, not Vodafone.`
          : "ICCID prefix does not match Vodafone (expected Vodafone issuer IIN).",
      );
    }
  }
}
